/* eslint-disable react/prop-types */
import { useEffect, useState } from 'react';

import {
  Badge,
  Button,
  Form,
  message,
  Popconfirm,
  Space,
  Table,
  Typography,
} from 'antd';

import { User } from '../../../types';
import { deleteUser, getUsers } from '../../../utils/httpRequest';
import Page from '../../../components/page';
import ActionHeader from '../../../components/actionHeader';
import { Content } from './styles';
import { roles } from '../../../constants';
import Icon from '@ant-design/icons';
import { SVGDelete, SVGEdit } from '../../../images';
import CreateUserModal from './createUserModal';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { fetchCompanies, selectCompanies } from '../../../slices/companies';
import { useAppDispatch } from '../../../store';

const UserList = () => {
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingUserId, setEditingUserId] = useState('');
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const companies = useSelector(selectCompanies);
  const [messageApi, contextHolder] = message.useMessage();

  const editingUser = users.find((i) => i.userId === editingUserId);

  const handleAddUser = () => {
    setEditingUserId('');
    setShowCreateUserModal(true);
  };

  const handleEditUser = async (record: User) => {
    setShowCreateUserModal(true);
    setEditingUserId(record.userId);
  };

  const getData = async () => {
    try {
      setLoading(true);
      const res: User[] = await getUsers();
      setUsers(_.reverse(res.map((i) => ({ ...i, role: i.roles[0].name }))));
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    try {
      setLoading(true);
      await deleteUser(userId);
      getData();
    } catch (err: any) {
      messageApi.error(err || 'Delete failed!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    if (!companies.length) {
      dispatch(fetchCompanies());
    }
  }, []);

  const columns = [
    {
      title: 'Full Name',
      dataIndex: 'username',
    },
    {
      title: 'Company',
      dateIndex: 'companyName',
      render: (value: string, record: User) => record?.companyName || '-',
    },
    { title: 'Email', dataIndex: 'email' },
    // {
    //   title: 'Initial Password',
    //   dataIndex: 'password',
    //   render: () => '-',
    // },
    {
      title: 'Role',
      dataIndex: 'role',
      render: (value: any) => {
        return roles.find((i) => i.value === value)?.label;
      },
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      render: (value: boolean) => {
        return (
          <Badge
            color={value ? '#52c41a' : '#ff4d4f'}
            text={value ? 'Active' : 'Deactivated'}
          />
        );
      },
    },
    {
      title: 'Action',
      dataIndex: 'action',
      render: (value: any, record: User, index: number) => {
        return (
          <Space>
            <Typography.Link onClick={() => handleEditUser(record)}>
              <Icon component={SVGEdit} /> Edit
            </Typography.Link>
            <Popconfirm
              placement="bottom"
              title={`Are you sure you want to delete ${record.username}?`}
              okText="Yes"
              cancelText="No"
              onConfirm={() => handleDeleteUser(record.userId)}
            >
              <Typography.Link type="danger" title="Delete">
                <Icon component={SVGDelete} />
              </Typography.Link>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <Page
      header={
        <ActionHeader
          title="User Management"
          titleTag={users.length ? `(${users.length})` : ''}
          endSlot={
            <Button type="primary" onClick={handleAddUser}>
              Add New User
            </Button>
          }
        />
      }
    >
      <Content>
        {contextHolder}
        <Form form={form} component={false}>
          <Table
            columns={columns}
            dataSource={users.map((i) => ({ key: i.userId, ...i }))}
            loading={loading}
          ></Table>
        </Form>
        <CreateUserModal
          open={showCreateUserModal}
          onClose={() => setShowCreateUserModal(false)}
          onConfirm={() => {
            setShowCreateUserModal(false);
            getData();
            setEditingUserId('');
          }}
          isEdit={!!editingUserId}
          user={editingUser}
        />
      </Content>
    </Page>
  );
};

export default UserList;
